import { Box, HStack, Text } from '@chakra-ui/react';
import React, { FC, useMemo } from 'react';

import { toOptionalFixed } from '@app/helpers/format';
import { PoolTokensCell } from '@app/screens/pools/components/PoolTokensCell';
import { BadgesConfig, Pool } from '@app/types/pool';
import { Campaign } from '@app/hooks/merkl/useMerklCampaigns';
import { MarketMakerCampaignWidget } from '@app/screens/pools/components/MarketMakerCampaignWidget';
import { useConfig } from '@app/config';
import { MetromCampaign } from '@app/hooks/metrom/useMetromCampaigns';
import { useMergedMarketMakerRewards } from '@app/hooks/metrom/useMergedMarketMakerRewards';
import { useAprWithTooltip } from '@app/hooks/metrom/useAprWithTooltip';
import { AprDetailsTooltip } from '@app/components/AprDetailsTooltip';
import { useAllProvidersVaults } from '@app/hooks/pools/useAllProvidersVaults';
import { formatUSD } from '@app/helpers/formatUSD';
import { LabeledValue } from '@app/screens/liquidity/LiquidityWizard/components/SelectTokensStep/components/LabeledValue';
import { StrategyProvider } from '@app/state/mintStore';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';

interface Props {
  item: Pool;
  apr: string | undefined;
  isVerified?: boolean;
  badges?: BadgesConfig;
  marketMakerCampaign?: {
    merkl: Array<Campaign & { amountUSD: number }>;
    metrom: Array<MetromCampaign & { amountUSD: number }>;
  };
  isSelectable?: boolean;
}

export const PoolDetails: FC<Props> = ({
  item,
  apr,
  isVerified,
  badges,
  marketMakerCampaign,
  isSelectable = true
}) => {
  const {
    flow,
    mode,
    currencyB,
    currencyA,
    actions: { setMode, setStrategy, setProvider, setFlow }
  } = useLiquidityWizardState();
  const { token0, token1, totalValueLockedUSD } = item;
  const { data } = useAllProvidersVaults();

  const tvl = useMemo(() => {
    if (!data) {
      return totalValueLockedUSD;
    }

    const p = data.filter(v => v.pool.toLowerCase() === item.id.toLowerCase());

    return p.reduce((r, k) => r + +(k.tvl || 0), +totalValueLockedUSD);
  }, [data, item.id, totalValueLockedUSD]);

  const merklAPR =
    marketMakerCampaign?.merkl
      .filter(c => c.campaignParameters.whitelist.length === 0)
      .reduce((acc: number, curr: Campaign) => {
        return acc + curr.apr;
      }, 0) ?? 0;

  const metromAPR =
    marketMakerCampaign?.metrom.reduce((acc: number, curr: MetromCampaign) => {
      return curr.apr ? acc + +curr.apr : acc;
    }, 0) ?? 0;

  const totalApr = Number(apr ?? 0) + merklAPR + metromAPR;

  const aprValues = useAprWithTooltip({
    totalApr,
    swapFeesApr: apr ? +apr : 0,
    marketMakerAPR: merklAPR + metromAPR
  });

  const marketMaker = useMergedMarketMakerRewards(
    marketMakerCampaign,
    undefined
  );
  const config = useConfig();

  const isSelected =
    ((currencyA?.wrapped.address.toLowerCase() === token0.id.toLowerCase() &&
      currencyB?.wrapped.address.toLowerCase() === token1.id.toLowerCase()) ||
      (currencyA?.wrapped.address.toLowerCase() === token1.id.toLowerCase() &&
        currencyB?.wrapped.address.toLowerCase() ===
          token0.id.toLowerCase())) &&
    mode === 'manual' &&
    flow === 'v4';

  return (
    <Box
      sx={{
        backgroundColor: 'gray.16',
        p: '24px',
        width: '100%',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '1px solid',
        borderColor: isSelectable && isSelected ? 'orange' : 'transparent'
      }}
      _hover={{
        filter: 'brightness(120%)'
      }}
      onClick={() => {
        setMode('manual');
        setFlow('v4');
        setProvider(null);
        setStrategy(null);
      }}
    >
      <PoolTokensCell
        token0={token0}
        token1={token1}
        isMain={isVerified}
        isNew={badges?.new}
        isVerifiedMeme={badges?.verified_meme}
        version={4}
      />
      <HStack gap="24px" width="100%" mt={6} alignItems="flex-start">
        <LabeledValue label="TVL" value={formatUSD.format(+tvl)} />
        {/*<LabeledValue label="VOLUME" value="volume???" />*/}
        <LabeledValue
          label="APR"
          value={
            <HStack alignItems="center" gap={1}>
              <Text fontSize="16px" color="white" mr={1}>
                {totalApr ? `${toOptionalFixed(+totalApr, 2)}%` : ''}
              </Text>
              {totalApr && <AprDetailsTooltip data={aprValues} />}
            </HStack>
          }
        />
        {config?.URLS.merklApiUrl || config?.URLS.metromApiUrl ? (
          <LabeledValue
            label="MM"
            value={
              marketMakerCampaign && (
                <MarketMakerCampaignWidget campaignData={marketMaker} />
              )
            }
          />
        ) : null}
      </HStack>
    </Box>
  );
};
